import type { NetworkLog, ActivityAction } from "../types"

// Check for pattern in potentially truncated response (handles JSON with/without spaces)
function responseContainsType(response: string | undefined, type: string): boolean {
  if (!response) return false
  // Match both "type":"value" and "type": "value" (with space)
  return response.includes(`"type":"${type}"`) || response.includes(`"type": "${type}"`)
}

/**
 * Detects user actions from network logs.
 * Maps API calls to semantic action names for activity tracking.
 * Based on original GlobalActivity.js lines 256-343.
 */
export function detectActions(logs: NetworkLog[]): ActivityAction[] {
  const actions: ActivityAction[] = []

  for (const log of logs) {
    let path: string
    try {
      const url = new URL(log.url, window.location.origin)
      path = url.pathname
    } catch {
      continue
    }

    const method = log.method.toLowerCase()
    const pathParts = path.split("/").length

    if (method === "post") {
      // Notes - only user-created notes, not system notes
      // Use string matching since JSON response may be truncated
      if (path === "/v1/notes") {
        if (responseContainsType(log.response, "user")) {
          actions.push("created_note")
        }
      }

      // Documents & AI
      if (path === "/v1/documents") actions.push("uploaded_document")
      if (path === "/v1/ai_chat_threads") actions.push("messaged_ai_chat")
      if (path.includes("/v1/ai_jobs") && pathParts === 5) actions.push("ran_ai_job")
      if (path.includes("/v1/ai_human_actions") && path.includes("resolve")) actions.push("resolved_human_action")

      // Contacts & Events
      if (path === "/v1/contacts") actions.push("created_contact")
      if (path.includes("/v1/contacts") && path.includes("password")) actions.push("updated_contact_password")
      if (path === "/v1/events") actions.push("created_event")
      if (path === "/v1/forms/request") actions.push("requested_form")

      // Billing
      if (path === "/v1/invoices") actions.push("created_invoice")
      if (path === "/v1/payments") actions.push("created_payment")
      if (path === "/v1/payments/refund") actions.push("refunded_payments")
      if (path === "/v1/payment_methods") actions.push("added_payment_method")
      if (path === "/v1/payment_subscriptions") actions.push("created_payment_subscription")
      if (path === "/v1/payment_trust_entries") actions.push("created_trust_entry")

      // Leads & Matters
      if (path.includes("/v1/leads") && path.includes("claim")) actions.push("claimed_lead")
      if (path === "/v1/matters") actions.push("created_matter")

      // Outstanding Items
      if (path === "/v1/outstanding_items") actions.push("created_item")
      if (path.includes("/v1/outstanding_items") && path.includes("finish")) actions.push("finished_item")

      // Portal & Signing
      if (path === "/v1/portal_entries") actions.push("enabled_client_portal_access")
      if (path === "/v1/signing/documents") actions.push("sent_document_for_signature")

      // Other
      if (path.includes("/v1/social_security_numbers") && pathParts === 4) actions.push("updated_client_ss_number")
      if (path === "/v1/support_messages") actions.push("sent_support_message")
      if (path.includes("/v1/tasks") && path.includes("finish")) actions.push("finished_task")
      if (path === "/v1/texts") actions.push("sent_text_message")
      if (path === "/v1/user_comments") actions.push("created_user_comment")

      // BK Module - POST
      if (path === "/v1/modules/bk/questionnaires/calculations/monthly_expenses")
        actions.push("bk_calculated_monthly_expenses")
      if (path === "/v1/modules/bk/questionnaires/calculations/vehicle_details")
        actions.push("bk_imported_vehicle_details")
      if (path === "/v1/modules/bk/questionnaires/calculations/import_lexus") actions.push("bk_imported_lexus_report")
      if (path.includes("/v1/modules/bk/questionnaires") && pathParts === 6) actions.push("bk_saved_questionnaire_fields")
      if (path.includes("/v1/modules/bk/questionnaires") && path.includes("qualify"))
        actions.push("bk_saved_questionnaire_onboarding")
      if (path === "/v1/modules/bk/credit_counseling_courses") actions.push("bk_uploaded_ccc")
      if (path.includes("/v1/modules/bk/cases") && pathParts === 6) actions.push("bk_synced_case_to_pacer")
      if (path.includes("/v1/modules/bk/cases") && path.includes("stage")) actions.push("bk_moved_stage")
      if (path === "/v1/modules/bk/credit_reports/download") actions.push("bk_pulled_credit_report")
      if (path === "/v1/modules/bk/bk_creditors") actions.push("bk_added_pre_filing_creditor")
    } else if (method === "patch") {
      // Voicemails
      if (path.includes("/v1/call_voicemails") && path.includes("resolve")) actions.push("resolved_voicemail")

      // Contacts & Events
      if (path.includes("/v1/contacts") && pathParts === 4) actions.push("updated_contact")
      if (path.includes("/v1/events") && pathParts === 4) actions.push("updated_event")
      if (path.includes("/v1/events") && path.includes("finish")) actions.push("finished_event")

      // Invoices & Documents
      if (path.includes("/v1/invoices") && pathParts === 4) actions.push("updated_invoice")
      if (path.includes("/v1/matter_document_uploads") && pathParts === 4) actions.push("updated_document_category")

      // Matters
      if (path.includes("/v1/matters") && path.includes("step")) actions.push("moved_matter_step")
      if (path.includes("/v1/matters") && path.includes("trust")) actions.push("changed_matter_trust_value")
      if (path.includes("/v1/matters/custom_dates") && pathParts === 4) actions.push("set_matter_custom_date")
      if (path.includes("/v1/matters/expected_dates") && pathParts === 4) actions.push("set_matter_expected_date")
      if (path.includes("/v1/matters/tags")) actions.push("added_matter_banner")

      // Notes & Items
      if (path.includes("/v1/notes") && pathParts === 4) actions.push("updated_note")
      if (path.includes("/v1/outstanding_items") && pathParts === 4) actions.push("updated_item")

      // Payments
      if (path.includes("/v1/payment_methods") && pathParts === 4) actions.push("updated_payment_method")
      if (path.includes("/v1/payment_subscriptions") && pathParts === 4) actions.push("updated_payment_subscription")
      if (path.includes("/v1/payment_trust_entries") && path.includes("reverse")) actions.push("reversed_trust_entry")

      // Portal & Comments
      if (path.includes("/v1/portal_entries") && pathParts === 4) actions.push("updated_client_portal_info")
      if (path.includes("/v1/user_comments") && pathParts === 4) actions.push("updated_user_comment")

      // BK Module - PATCH
      if (path.includes("/v1/modules/bk/credit_counseling_courses")) actions.push("bk_updated_ccc_status")
      if (path.includes("/v1/modules/bk/cases") && pathParts === 6) actions.push("bk_updated_module_fields")
      if (path.includes("/v1/modules/bk/cases") && path.includes("hearing_result")) actions.push("bk_added_hearing_result")
      if (path.includes("/v1/modules/bk/bk_creditors") && pathParts === 6) actions.push("bk_updated_pre_filing_creditor")
    } else if (method === "delete") {
      if (path.includes("/v1/payment_methods") && pathParts === 4) actions.push("removed_payment_method")
      if (path.includes("/v1/payment_subscriptions") && pathParts === 4) actions.push("deleted_payment_subscription")
      if (path.includes("/v1/user_comments") && pathParts === 4) actions.push("deleted_user_comment")
      if (path.includes("/v1/matters/tags")) actions.push("removed_matter_banner")

      // BK Module - DELETE
      if (path.includes("/v1/modules/bk/bk_creditors") && pathParts === 6) actions.push("bk_deleted_pre_filing_creditor")
    } else if (method === "get") {
      if (path.includes("/v1/documents") && path.includes("download")) actions.push("downloaded_document")
    }
  }

  // Dedupe actions
  return Array.from(new Set(actions))
}
